import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Play, CheckCircle2, Clock, BookOpen, Headphones, GraduationCap, Users } from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { usePodcast } from "../lib/PodcastContext";
import { useAuth } from "../contexts/AuthContext";

const LEVEL_LABELS = {
    beginner: { fr: "Débutant", color: "#FCDB53" },
    intermediate: { fr: "Intermédiaire", color: "#F89F63" },
    advanced: { fr: "Avancé", color: "#EE722E" },
    expert: { fr: "Expert", color: "#EE722E" },
};

// Iter-27.52 — Carte module extraite pour le toggle audience.
function ModuleCard({ m, idx }) {
    const meta = LEVEL_LABELS[m.level] || LEVEL_LABELS.beginner;
    const podcast = usePodcast();
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: idx * 0.05 }}
            className={`relative rounded-3xl overflow-hidden group glass-card glass-card-hover transition-all ${
                !m.unlocked ? "opacity-60" : ""
            }`}
            data-testid={`module-card-${m.module_id}`}
        >
            <div className="relative aspect-[16/9] overflow-hidden">
                {m.cover_image && (
                    <img
                        src={m.cover_image}
                        alt={m.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-shariti-black via-shariti-black/40 to-transparent" />
                <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                    <span
                        className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full"
                        style={{ background: meta.color, color: "#181818" }}
                    >
                        {meta.fr}
                    </span>
                    {/* Iter-27.59 — Badge admin override pour traçabilité visuelle */}
                    {m.admin_override === "unlock" && (
                        <span
                            data-testid={`module-override-unlock-${m.module_id}`}
                            className="text-[10px] font-extrabold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full inline-flex items-center gap-1 bg-shariti-yellow text-shariti-black"
                            title="Module débloqué par l'administration"
                        >
                            🔓 Accès admin
                        </span>
                    )}
                    {m.admin_override === "lock" && (
                        <span
                            data-testid={`module-override-lock-${m.module_id}`}
                            className="text-[10px] font-extrabold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full inline-flex items-center gap-1 bg-red-500 text-white"
                            title="Module verrouillé par l'administration"
                        >
                            🔒 Bloqué admin
                        </span>
                    )}
                    {m.bonus && (
                        <span
                            className="text-[10px] font-extrabold uppercase tracking-[0.2em] px-3 py-1 rounded-full inline-flex items-center gap-1 text-white"
                            style={{
                                background: "linear-gradient(135deg, #EE722E 0%, #F89F63 100%)",
                                boxShadow: "0 4px 18px rgba(238,114,46,0.55), 0 0 0 1px rgba(255,255,255,0.18) inset",
                            }}
                            data-testid={`module-bonus-badge-${m.module_id}`}
                        >
                            ★ Bonus élite
                        </span>
                    )}
                    {m.completed && (
                        <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-shariti-orange text-white inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Terminé
                        </span>
                    )}
                </div>
                {!m.unlocked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-shariti-black/60 backdrop-blur-sm">
                        <div className="text-center">
                            <div className="w-14 h-14 mx-auto rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-2">
                                <Lock className="w-6 h-6 text-white/70" />
                            </div>
                            <p className="text-xs font-bold text-white/70 uppercase tracking-widest">
                                Verrouillé
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-5">
                <h3 className="text-xl font-extrabold text-white tracking-tight mb-2">
                    {m.title}
                </h3>
                <p className="text-sm text-white/55 font-light mb-4 min-h-[2.5rem]">
                    {m.description}
                </p>

                <div className="flex items-center gap-4 text-xs text-white/50 mb-4">
                    <span className="inline-flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {m.duration_minutes} min
                    </span>
                    {m.best_score != null && (
                        <span className="inline-flex items-center gap-1 text-shariti-orange-light">
                            <BookOpen className="w-3.5 h-3.5" />
                            Score {m.best_score}%
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2 mb-4">
                    <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-shariti-orange rounded-full transition-all duration-500"
                            style={{ width: `${m.progress_percent || 0}%` }}
                        />
                    </div>
                    <span className="text-xs font-bold text-white">
                        {m.progress_percent || 0}%
                    </span>
                </div>

                {m.unlocked ? (
                    <div className="space-y-2">
                        <Link
                            to={`/module/${m.module_id}`}
                            data-testid={`open-module-${m.module_id}`}
                            className="inline-flex w-full items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-2.5 rounded-2xl hover:bg-shariti-orange-light transition-all"
                        >
                            <Play className="w-4 h-4 fill-white" />
                            {m.completed
                                ? "Revoir le module"
                                : m.progress_percent > 0
                                ? "Lancer mon entraînement"
                                : "Devenir performant"}
                        </Link>
                        <button
                            onClick={() => podcast.start(m.module_id)}
                            data-testid={`podcast-launch-${m.module_id}`}
                            disabled={podcast.isActive && podcast.moduleId === m.module_id}
                            className="inline-flex w-full items-center justify-center gap-1.5 bg-white/5 hover:bg-white/12 text-white/85 hover:text-white font-bold py-2 rounded-2xl border border-shariti-orange/30 text-xs transition-all disabled:opacity-50"
                            title="Écouter ce module en continu (idéal trajet domicile-terrain)"
                        >
                            <Headphones className="w-3.5 h-3.5 text-shariti-orange-light" />
                            {podcast.isActive && podcast.moduleId === m.module_id
                                ? "Podcast en cours..."
                                : "Mode podcast"}
                        </button>
                    </div>
                ) : (
                    <button
                        disabled
                        className="w-full text-white/40 font-bold py-2.5 rounded-2xl bg-white/5 cursor-not-allowed"
                    >
                        Termine le précédent
                    </button>
                )}
            </div>
        </motion.div>
    );
}

// Iter-27.51 + 27.59 — Unlock séquentiel calculé SUR UNE AUDIENCE séparément.
// Iter-27.59 — Respecte l'admin_override ("unlock" force l'accès, "lock" force le blocage)
// retourné par /api/modules pour ce user (cf. iter-27.55).
function buildUnlockMap(modulesOfAudience) {
    const map = {};
    modulesOfAudience.forEach((m, i) => {
        // Override admin = priorité absolue sur la logique séquentielle
        if (m.admin_override === "lock") {
            map[m.module_id] = false;
            return;
        }
        if (m.admin_override === "unlock") {
            map[m.module_id] = true;
            return;
        }
        const prev = i > 0 ? modulesOfAudience[i - 1] : null;
        map[m.module_id] =
            m.unlock_for_all === true ||
            i === 0 ||
            !prev ||
            prev.completed ||
            (m.progress_percent || 0) > 0;
    });
    return map;
}

export default function Parcours() {
    const [modules, setModules] = useState([]);
    const [filter, setFilter] = useState("all");
    const { user } = useAuth();

    useEffect(() => {
        api.get("/modules").then((r) => setModules(r.data));
    }, []);

    // Iter-30.54 — Rafraîchir la liste des modules quand le podcast valide
    // une leçon ou se termine, pour que les barres de progression et l'état
    // verrouillé/déverrouillé soient à jour sans reload.
    useEffect(() => {
        const handler = () => {
            api.get("/modules").then((r) => setModules(r.data)).catch(() => { /* */ });
        };
        window.addEventListener("shariti:podcast-lesson-completed", handler);
        window.addEventListener("shariti:podcast-finished", handler);
        return () => {
            window.removeEventListener("shariti:podcast-lesson-completed", handler);
            window.removeEventListener("shariti:podcast-finished", handler);
        };
    }, []);

    const role = (user?.role || "learner").toLowerCase();
    const showsBoth = role === "manager" || role === "admin";

    // Iter-27.52 — Toggle audience (au lieu de 2 sections empilées) :
    //   - manager → démarre sur "manager" (son parcours dédié)
    //   - admin → démarre sur "recruiter" (catalogue historique)
    //   - learner → pas de toggle (uniquement recruiter)
    const [audienceTab, setAudienceTab] = useState(() => {
        if (role === "manager") return "manager";
        return "recruiter";
    });

    // Iter-27.51 — Split par audience pour unlock indépendant
    const { recruiterMods, managerMods } = useMemo(() => {
        const sorted = [...modules].sort((a, b) => (a.order || 0) - (b.order || 0));
        const rec = sorted.filter((m) => (m.audience || "recruiter") === "recruiter");
        const mgr = sorted.filter((m) => m.audience === "manager");
        const unlockRec = buildUnlockMap(rec);
        const unlockMgr = buildUnlockMap(mgr);
        return {
            recruiterMods: rec.map((m) => ({ ...m, unlocked: unlockRec[m.module_id] !== false })),
            managerMods: mgr.map((m) => ({ ...m, unlocked: unlockMgr[m.module_id] !== false })),
        };
    }, [modules]);

    const applyFilter = (list) => {
        if (filter === "bonus") return list.filter((m) => !!m.bonus);
        if (filter !== "all") return list.filter((m) => m.level === filter);
        return list;
    };

    // Sélection : pour learner toujours recruiter ; pour manager/admin selon toggle
    const activeList = showsBoth
        ? (audienceTab === "manager" ? managerMods : recruiterMods)
        : recruiterMods;
    const visibleList = applyFilter(activeList);

    const pageTitle = "Parcours";
    const pageSubtitle = role === "manager"
        ? (audienceTab === "manager"
            ? "Tes modules dédiés management terrain"
            : "Rafraîchis ce que tes recruteurs apprennent")
        : role === "admin"
        ? "Catalogue complet · " + (audienceTab === "manager" ? "Référents" : "Recruteurs")
        : "Progresse étape par étape";

    return (
        <Layout title={pageTitle} subtitle={pageSubtitle}>
            {/* ─── Iter-27.52 — TOGGLE PARCOURS (manager + admin uniquement) ─── */}
            {showsBoth && (
                <div className="mb-6" data-testid="parcours-audience-toggle">
                    <div className="inline-flex bg-white/5 border border-white/10 rounded-2xl p-1.5 gap-1">
                        <button
                            onClick={() => setAudienceTab("manager")}
                            data-testid="parcours-tab-manager"
                            className={`px-4 sm:px-5 py-2.5 rounded-xl text-xs sm:text-sm font-extrabold uppercase tracking-wider transition-all inline-flex items-center gap-2 ${
                                audienceTab === "manager"
                                    ? "bg-shariti-yellow text-shariti-black"
                                    : "text-white/55 hover:text-white"
                            }`}
                        >
                            <Users className="w-4 h-4" />
                            Référents
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full tabular-nums ${
                                audienceTab === "manager" ? "bg-shariti-black/20 text-shariti-black" : "bg-white/10 text-white/60"
                            }`}>
                                {managerMods.length}
                            </span>
                        </button>
                        <button
                            onClick={() => setAudienceTab("recruiter")}
                            data-testid="parcours-tab-recruiter"
                            className={`px-4 sm:px-5 py-2.5 rounded-xl text-xs sm:text-sm font-extrabold uppercase tracking-wider transition-all inline-flex items-center gap-2 ${
                                audienceTab === "recruiter"
                                    ? "bg-shariti-orange text-white orange-glow"
                                    : "text-white/55 hover:text-white"
                            }`}
                        >
                            <GraduationCap className="w-4 h-4" />
                            Recruteurs
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full tabular-nums ${
                                audienceTab === "recruiter" ? "bg-black/25 text-white" : "bg-white/10 text-white/60"
                            }`}>
                                {recruiterMods.length}
                            </span>
                        </button>
                    </div>
                    {role === "manager" && (
                        <p className="text-[11px] text-white/40 mt-2 leading-relaxed">
                            {audienceTab === "manager"
                                ? "Ces modules sont dédiés au pilotage d'équipe et n'apparaissent que pour les référents."
                                : "Tu accèdes ici à la formation suivie par tes recruteurs — utile pour rafraîchir les fondamentaux et coacher."}
                        </p>
                    )}
                </div>
            )}

            {/* ─── FILTRES DE NIVEAU ─── */}
            <div
                className="mb-6 overflow-x-auto scrollbar-hide -mx-1 px-1"
                data-testid="parcours-filters-scroll"
            >
                <div
                    className="flex gap-2 p-1.5 glass-card rounded-full w-max"
                    data-testid="parcours-filters"
                >
                    {[
                        { id: "all", label: "Tout" },
                        { id: "beginner", label: "Débutant" },
                        { id: "intermediate", label: "Intermédiaire" },
                        { id: "advanced", label: "Avancé" },
                        { id: "expert", label: "Expert" },
                        { id: "bonus", label: "Bonus" },
                    ].map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            data-testid={`filter-${f.id}`}
                            className={`px-4 lg:px-5 py-2 rounded-full text-xs lg:text-sm font-bold whitespace-nowrap transition-all ${
                                filter === f.id
                                    ? "bg-shariti-orange text-white"
                                    : "text-white/55 hover:text-white"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ─── GRILLE DE MODULES (1 audience à la fois) ─── */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={`${audienceTab}-${filter}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                    className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5"
                >
                    {visibleList.map((m, i) => (
                        <ModuleCard key={m.module_id} m={m} idx={i} />
                    ))}
                </motion.div>
            </AnimatePresence>

            {visibleList.length === 0 && (
                <div className="text-center text-white/40 py-16" data-testid="parcours-empty">
                    Aucun module ne correspond à ce filtre pour le moment.
                </div>
            )}
        </Layout>
    );
}
